<template>
  <div :class="['form-element', hidden && 'd-none']">
    <FormLabel
      v-if="label"
      v-bind="{
        label,
        required,
        disabled,
        description,
        descriptionType,
        dirty,
        error,
        id,
        status
      }"
    >
      <template #label-after>
        <AppIcon
          v-if="isSuccess"
          name="check_circle"
          :size="16"
          class="ml-2 text-success icon-align"
        />
        <AppIcon
          v-else-if="isChecking"
          name="cached"
          :size="16"
          class="ml-2 icon-align spin"
        />
        <AppIcon
          v-else-if="isError"
          name="cancel"
          :size="16"
          class="ml-2 icon-align text-danger"
        />
      </template>
    </FormLabel>
    <div v-if="readOnly">
      {{ value }}
    </div>
    <template v-else>
      <input
        :id="id"
        v-model="currentValue"
        v-tooltip.bottom="{
          content: 'This course number is already in use',
          show: isError,
          trigger: 'manual',
          classes: 'tooltip-danger',
          boundariesElement: 'body',
        }"
        :type="config.type"
        :placeholder="disabled ? '—' : placeholder"
        :disabled="disabled"
        :class="[
          'form-control',
          disabled && 'p-0',
          inline && 'form-text--inline',
        ]"
        @input="validate($event.target.value)"
      />
    </template>
  </div>
</template>

<script>
import { inject, ref, computed, toRefs } from '@vue/composition-api'
import FormMixin from './FormMixin'
import _debounce from 'lodash/debounce'
import { DEBOUNCE_INTERVAL } from '@/constants/'
import { getCmCourseNumberValidation } from '@/api'
import { mapGetters } from 'vuex'
import FormLabel from './FormLabel'

export default {
  mixins: [ FormMixin ],
  components: { FormLabel },
  props: {
    config: {
      type: Object,
      default: () => ({ type: 'text' }),
    },
    inline: {
      type: Boolean,
      default: false,
    },
    formValue: {
      type: Object,
      default: () => ({}),
    },
  },
  data () {
    return {
      isSuccess: false,
      isError: false,
      isChecking: false,
      currentValue: null,
    }
  },
  setup (props) {
    // TODO: Get rid of this inject and use only formValue at some point
    const providerData = inject('providerData', ref({
      questions: {},
      values: {},
      courseId: '',
    }))

    const { formValue } = toRefs(props)
    const courseId = inject('courseId', computed(() => formValue?.value._id || providerData.value.courseId))

    return {
      providerData,
      courseId,
    }
  },
  computed: {
    ...mapGetters([ 'school' ]),
    subjectCode () {
      if (this.formValue.hasOwnProperty('subjectCode')) {
        return this.formValue.subjectCode
      }
      const question = Object
        .values(this.providerData.questions)
        .find(q => q.questionId === 'subjectCode')
      return this.providerData.values[question?.questionId] || ''
    },

  },
  watch: {
    subjectCode () {
      this.validate(this.value)
    },
    value (v) {
      this.currentValue = v
      if (v === undefined) {
        this.isChecking = false
        this.isSuccess = false
        this.isError = false
      }
    },
  },
  created () {
    this.currentValue = this.value
  },
  methods: {
    validate (value) {
      if (this.disabled) return
      this.isChecking = true
      this.isSuccess = false
      this.isError = false
      this.validatingDebounced()
    },
    validatingDebounced: _debounce(function () {
      this.validateCourseNumber()
    }, DEBOUNCE_INTERVAL),
    async validateCourseNumber () {
      const currentValue = this.currentValue || ''
      if (this.subjectCode && currentValue.trim()) {
        const { status } = await getCmCourseNumberValidation({
          school: this.school,
          subjectCode: this.subjectCode,
          courseNumber: this.currentValue,
          courseId: this.courseId,
        })
        this.isSuccess = status === 'valid'
        this.isError = !this.isSuccess
        if (this.isSuccess && status) {
          this.$emit('input', this.currentValue)
        }
      }
      this.isChecking = false
    },
  },
}
</script>

<style scoped>
.form-control {
  height: 36px;
}

.form-text--inline:not(:focus):not(:hover) {
  border-color: transparent;
  background-color: transparent;
}
.icon-align {
  line-height: 12px;
}
.spin {
  animation: spin 2000ms infinite linear;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
